/**
 * BaseRepository<T>
 * 
 * Generic repository pattern for CRUD operations using DatabaseService.
 * Extend this class for entity-specific repositories (e.g., AlertRepository, NotificationRepository).
 * 
 * @template T
 */
class BaseRepository {
  /**
   * @param {DatabaseService} dbService - Instance of DatabaseService
   * @param {string} storeName - Name of the object store
   */
  constructor(dbService, storeName) {
    this.dbService = dbService;
    this.storeName = storeName;
  }

  /**
   * Create a new entity
   * @param {T} entity
   * @returns {Promise<IDBValidKey>}
   */
  async create(entity) {


    try {
      const result = await this.dbService.add(this.storeName, entity);

      return result;
    } catch (error) {
      console.error(`[BaseRepository] Create failed in ${this.storeName}:`, error);


      throw error;
    }
  }

  /**
   * Get entity by key
   * @param {IDBValidKey} key
   * @returns {Promise<T|null>}
   */
  async get(key) {

    try {
      const result = await this.dbService.get(this.storeName, key);


      return result;
    } catch (error) {
      console.error(`[BaseRepository] Get failed in ${this.storeName} for key ${key}:`, error);

      throw error;
    }
  }

  /**
   * Update entity
   * @param {IDBValidKey|T} idOrEntity - Either the ID of the entity to update or the complete entity with ID
   * @param {T} [entity] - The updated entity (if idOrEntity is an ID)
   * @returns {Promise<IDBValidKey>}
   */
  async update(idOrEntity, entity) {
    try {
      // If two parameters are provided, first is ID and second is entity
      if (arguments.length === 2) {
        const existingEntity = await this.get(idOrEntity);
        if (!existingEntity) {
          throw new Error(`Entity with ID ${idOrEntity} not found`);
        }
        const updatedEntity = { ...existingEntity, ...entity, id: idOrEntity };
        return await this.dbService.put(this.storeName, updatedEntity);
      } 
      // If only one parameter is provided, it's the complete entity
      else {
        if (!idOrEntity.id) {
          throw new Error('Entity must have an ID property for update operation');
        }
        return await this.dbService.put(this.storeName, idOrEntity);
      }
    } catch (error) {
      console.error(`[BaseRepository] Update failed:`, error);
      throw error;
    }
  }

  /**
   * Delete entity by key
   * @param {IDBValidKey} key
   * @returns {Promise<void>}
   */
  async delete(key) {
    try {
      return await this.dbService.delete(this.storeName, key);
    } catch (error) {
      console.error(`[BaseRepository] Delete failed:`, error);
      throw error;
    }
  }

  /**
   * Get all entities
   * @returns {Promise<T[]>}
   */
  async findAll() {

    try {
      const results = await this.dbService.getAll(this.storeName);


      return results;
    } catch (error) {
      console.error(`[BaseRepository] FindAll failed in ${this.storeName}:`, error);

      throw error;
    }
  }
}

export default BaseRepository;
